import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import {
  useFeatureViewCreateMutation,
  useFeatureViewDeleteMutation,
  useFeatureViewUpdateMutation,
} from "@/app/data/useFeatureViewMutations";
import { useFeatureViewsData } from "@/app/data/useFeatureViewsData";
import {
  DeleteIconButton,
  EditIconButton,
  GoToIconButton,
} from "@/common/components/CommonIconButtons";
import FloatingFormError from "@/common/components/Form/FloatingFormError";
import FormCancel from "@/common/components/Form/FormCancel";
import FormInput from "@/common/components/Form/FormInput";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { useFeatureViewParam } from "@/common/hooks/useFeatureViewParam";
import {
  CreateAffectEnvironments,
  DeleteAffectEnvironments,
} from "@/environment/components/EnvironmentCallouts";
import { getFlagViewDisplayName } from "@/feature/utils/flagView";
import {
  ListLayout,
  ListLayoutProps,
} from "@/global-settings/components/GlobalSettingsHelpers";
import { useFlag } from "@reflag/react-sdk";
import {
  CreateFeatureViewArgsSchema,
  CreateFeatureViewArgsType,
  FeatureViewDTO,
  PatchFeatureViewArgsSchema,
  PatchFeatureViewArgsType,
} from "@reflag/shared/featureViewAPI";
import { FeatureViewUrl } from "@reflag/shared/urls";

const Layout = (props: ListLayoutProps) => (
  <ListLayout
    templateColumns="2fr 2fr minmax(80px, 1fr) minmax(150px, 1fr)"
    {...props}
  />
);

function ViewItem({ view }: { view: FeatureViewDTO }) {
  const { env } = useEnvironment();

  const form = useForm<PatchFeatureViewArgsType>({
    resolver: zodResolver(PatchFeatureViewArgsSchema),
    mode: "onChange",
    defaultValues: {
      name: view.name,
    },
  });

  const mutation = useFeatureViewUpdateMutation(view.id);

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    mutation,
    ({ name }) => {
      form.reset({ name });

      setShowEditForm(false);
    },
    {
      successToast: "View updated",
    },
  );

  const [showEditForm, setShowEditForm] = useState(false);

  return !showEditForm ? (
    <Layout key={view.id} showAsCard>
      <Text fontSize="md" isTruncated>
        {getFlagViewDisplayName(view)}
      </Text>
      <Box />
      <Box />
      <Box justifySelf="end">
        {view.type === "custom" && (
          <EditIconButton
            label="Edit view"
            onClick={() => setShowEditForm(true)}
          />
        )}
        {view.type === "custom" && <DeleteView view={view} />}
        <GoToIconButton label="Open view" to={FeatureViewUrl(env, view.id)} />
      </Box>
    </Layout>
  ) : (
    <FormProvider {...form}>
      <ViewForm
        submitLabel="Save"
        onClose={() => {
          setShowEditForm(false);
          form.reset();
        }}
        onSubmit={handleSubmit}
      />
    </FormProvider>
  );
}

function DeleteView({ view }: { view: FeatureViewDTO }) {
  const [storedViewId, updateFeatureViewParam] = useFeatureViewParam();

  const toast = useToast();
  const errorToast = useErrorToast();
  const { mutate: deleteView, isPending: isDeleteLoading } =
    useFeatureViewDeleteMutation(view.id, {
      onSuccess: () => {
        if (view.id === storedViewId) {
          updateFeatureViewParam(null);
        }

        toast({
          title: "Deleted view",
          status: "success",
        });
      },

      onError: () => {
        errorToast({
          title: "Couldn't delete view",
        });
      },
    });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  return (
    <>
      <DeleteIconButton
        isDisabled={isDeleteLoading}
        label="Delete view"
        onClick={onOpen}
      />
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete view</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete <strong>{view.name}</strong> ?
              <br />
              You can&apos;t undo this action afterwards.
              <DeleteAffectEnvironments entity="view" mt={8} />
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={isDeleteLoading}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={isDeleteLoading}
                  onClick={() => deleteView()}
                >
                  Delete
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

function ViewsList() {
  const { data, isLoading } = useFeatureViewsData();

  const { isEnabled: archivingEnabled } = useFlag("archiving");

  const featureViews = useMemo(() => {
    const allViews = data?.filter((view) => view.type === "all") ?? [];
    const archivedViews =
      data?.filter((view) => view.type === "archived") ?? [];
    const staleViews = data?.filter((view) => view.type === "stale") ?? [];
    const customViews = data?.filter((view) => view.type === "custom") ?? [];

    const archivingViews = archivingEnabled
      ? [...staleViews, ...archivedViews]
      : [];

    return [...allViews, ...archivingViews, ...customViews];
  }, [data, archivingEnabled]);

  if (isLoading) return null;

  return (
    <>
      <Layout
        color="dimmed"
        fontSize="sm"
        fontWeight="medium"
        minH="auto"
        py={1}
      >
        <Box>Name</Box>
      </Layout>
      {data?.length === 0 ? (
        <Layout showAsCard>
          <Box color="gray.500" fontSize="sm">
            No views yet
          </Box>
        </Layout>
      ) : (
        featureViews?.map((view) => <ViewItem key={view.id} view={view} />)
      )}
    </>
  );
}

function ViewForm({
  onClose,
  onSubmit,
  submitLabel,
}: {
  onClose: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  submitLabel: string;
}) {
  return (
    <form onSubmit={onSubmit}>
      <Layout py={4} showAsCard>
        <Box>
          <FloatingFormError name="name">
            <Box>
              <FormInput
                autoComplete="off"
                name="name"
                placeholder="Name your view"
                showErrors={false}
                autoFocus
                data-1p-ignore
              />
            </Box>
          </FloatingFormError>
        </Box>
        <Box />
        <Box />
        <Box justifySelf="end">
          <FormRootError />
          <ButtonGroup>
            <FormCancel onClick={onClose} />
            <FormSubmit>{submitLabel}</FormSubmit>
          </ButtonGroup>
        </Box>
      </Layout>
    </form>
  );
}

function CreateView() {
  const form = useForm<CreateFeatureViewArgsType>({
    resolver: zodResolver(CreateFeatureViewArgsSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
    },
  });

  const mutation = useFeatureViewCreateMutation();

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    mutation,
    () => {
      form.reset({
        name: "",
      });
      setShowCreateForm(false);
    },
    {
      successToast: "View created",
    },
  );

  const [showCreateForm, setShowCreateForm] = useState(false);

  return !showCreateForm ? (
    <Layout px={0}>
      <Box>
        <Button variant="outline" onClick={() => setShowCreateForm((c) => !c)}>
          Add view
        </Button>
      </Box>
    </Layout>
  ) : (
    <FormProvider {...form}>
      <ViewForm
        submitLabel="Create"
        onClose={() => {
          setShowCreateForm(false);
          form.reset();
        }}
        onSubmit={handleSubmit}
      />
    </FormProvider>
  );
}

export default function FeatureViews() {
  return (
    <Flex direction="column" gap={4} maxW="wideForm">
      <Restricted alertStyle="callout" role="contributor">
        <Flex direction="column" gap={2}>
          <CreateAffectEnvironments entity="views" mb={4} />
          <ViewsList />
          <CreateView />
        </Flex>
      </Restricted>
    </Flex>
  );
}
